import './App.css';
import { useEffect, useState, useCallback } from 'react';
import DashboardHeader from './dashboardheader.js';
import VerticalNavigation from './VerticalNavigation.js';
import bhcalc from './bhcalc.js';
import bhs from './bhs.js';

function LocationPrograms() {
  const userRole = bhs(state => state.userRole);
  const isPartner = userRole === 'partner';
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('active');

  const fetchPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await bhcalc.get('/program-location');
      setPrograms(data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Your account is not assigned to a location. Please log out and back in, or contact your administrator.');
      } else {
        setError('Failed to load programs.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [fetchPrograms]);

  const today = new Date().toISOString().slice(0, 10);

  const isEffectivelyArchived = (lp) => {
    if (lp.isArchivedOverride !== null && lp.isArchivedOverride !== undefined) {
      return lp.isArchivedOverride;
    }
    if (lp.program?.isArchived) return true;
    const effectiveEndDate = lp.endDateOverride ?? lp.program?.endDate;
    if (effectiveEndDate && effectiveEndDate < today) return true;
    return false;
  };

  const activePrograms = programs.filter((lp) => !isEffectivelyArchived(lp));
  const archivedPrograms = programs.filter((lp) => isEffectivelyArchived(lp));
  const displayed = isPartner ? activePrograms : (tab === 'active' ? activePrograms : archivedPrograms);

  return (
    <div className="App min-h-screen">
      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <h2 className="text-2xl font-bold text-left mb-6">Programs</h2>

            {!isPartner && (
              <div className="border-b border-gray-200 mb-4">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setTab('active')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      tab === 'active'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Active ({activePrograms.length})
                  </button>
                  <button
                    onClick={() => setTab('archived')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm ${
                      tab === 'archived'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Archived ({archivedPrograms.length})
                  </button>
                </nav>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Program Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Start Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Image</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-left">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">Loading programs...</td>
                    </tr>
                  ) : error ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-red-600">{error}</td>
                    </tr>
                  ) : displayed.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                        No {isPartner ? '' : tab + ' '}programs found.
                      </td>
                    </tr>
                  ) : (
                    displayed.map((lp) => (
                      <tr key={lp.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lp.program?.programName || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{lp.program?.programType?.name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{lp.startDateOverride || lp.program?.startDate || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{lp.endDateOverride || lp.program?.endDate || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {lp.program?.imageUrl ? (
                            <img src={lp.program.imageUrl} alt={lp.program.programName} className="h-8 w-8 object-cover rounded" />
                          ) : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {displayed.length > 0 && (
              <div className="mt-4">
                <span className="text-sm text-gray-600">
                  Showing {displayed.length} {isPartner ? '' : tab + ' '}program{displayed.length !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LocationPrograms;
